import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Users, X, ChevronRight, Download } from 'lucide-react';
import api from '../services/api';

const MATURITY_STYLES = {
  Emerging: 'bg-red-500 text-white',
  Developing: 'bg-[#f5a623] text-black',
  Established: 'bg-lime text-black',
  Mature: 'bg-ink text-lime',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return iso;
  }
};

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .getHealthCheckLeads()
      .then((data) => {
        if (active) setLeads(data.leads || []);
      })
      .catch((err) => {
        if (active) setError(err.message || 'Could not load leads.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const avgScore = useMemo(() => {
    if (!leads.length) return 0;
    const sum = leads.reduce((s, l) => s + (l.overall_percent || 0), 0);
    return Math.round(sum / leads.length);
  }, [leads]);

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 flex items-center justify-center bg-lime border border-ink/10 rounded-lg">
          <Users size={20} />
        </div>
        <h1 className="font-bold tracking-tight text-3xl md:text-4xl">Health Check Leads</h1>
      </div>
      <p className="font-medium text-gray-700 mb-8">
        Everyone who completed the public Governance Health Check.
      </p>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        <div className="border border-ink/10 rounded-lg shadow-saas bg-white p-5">
          <p className="font-bold uppercase text-xs tracking-wide text-gray-500">Total leads</p>
          <p className="font-bold tracking-tight text-4xl mt-1">{leads.length}</p>
        </div>
        <div className="border border-ink/10 rounded-lg shadow-saas bg-white p-5">
          <p className="font-bold uppercase text-xs tracking-wide text-gray-500">Avg. score</p>
          <p className="font-bold tracking-tight text-4xl mt-1">{avgScore}%</p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-3 font-bold text-lg py-16 justify-center">
          <Loader2 className="w-6 h-6 animate-spin" /> Loading leads…
        </div>
      )}

      {error && (
        <div className="border border-ink/10 rounded-lg shadow-saas bg-red-500 text-white p-6 font-bold">
          {error}
        </div>
      )}

      {!loading && !error && leads.length === 0 && (
        <div className="border border-ink/10 rounded-lg shadow-saas bg-white p-10 text-center">
          <p className="font-bold text-lg">No leads yet.</p>
          <p className="font-medium text-gray-600 mt-1">
            Leads appear here when visitors complete the public health check.
          </p>
        </div>
      )}

      {!loading && !error && leads.length > 0 && (
        <div className="border border-ink/10 rounded-lg shadow-saas bg-white overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-ink/10 bg-cream">
                <th className="p-4 font-bold uppercase text-xs tracking-wide">Name</th>
                <th className="p-4 font-bold uppercase text-xs tracking-wide">Organisation</th>
                <th className="p-4 font-bold uppercase text-xs tracking-wide hidden md:table-cell">Email</th>
                <th className="p-4 font-bold uppercase text-xs tracking-wide">Score</th>
                <th className="p-4 font-bold uppercase text-xs tracking-wide hidden sm:table-cell">Maturity</th>
                <th className="p-4 font-bold uppercase text-xs tracking-wide hidden lg:table-cell">Submitted</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr
                  key={lead.id}
                  className="border-b border-ink/10 hover:bg-lime/10 cursor-pointer"
                  onClick={() => setSelected(lead)}
                >
                  <td className="p-4 font-bold">{lead.full_name}</td>
                  <td className="p-4 font-medium">{lead.organisation_name}</td>
                  <td className="p-4 font-medium hidden md:table-cell">{lead.email}</td>
                  <td className="p-4 font-bold">{lead.overall_percent}%</td>
                  <td className="p-4 hidden sm:table-cell">
                    <span
                      className={`inline-block ${
                        MATURITY_STYLES[lead.maturity_level] || 'bg-gray-200'
                      } border border-ink/10 rounded-lg px-2 py-0.5 font-bold uppercase text-xs`}
                    >
                      {lead.maturity_level}
                    </span>
                  </td>
                  <td className="p-4 font-medium text-sm hidden lg:table-cell">
                    {formatDate(lead.submitted_at)}
                  </td>
                  <td className="p-4 text-right">
                    <ChevronRight size={18} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <LeadDetail lead={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};

const LeadDetail = ({ lead, onClose }) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const handleDownload = async () => {
    setDownloadError('');
    setDownloading(true);
    try {
      await api.downloadHealthCheckReport(lead.id);
    } catch (err) {
      setDownloadError(err.message || 'Could not generate the PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
  <div
    className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4"
    onClick={onClose}
  >
    <div
      className="bg-cream border border-ink/10 rounded-lg shadow-saas max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="border-b border-ink/10 p-5 flex items-center justify-between sticky top-0 bg-cream">
        <h2 className="font-bold tracking-tight text-2xl">{lead.full_name}</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={handleDownload}
            disabled={downloading}
            className={`border border-ink/10 rounded-lg px-4 py-2 font-bold flex items-center gap-2 text-sm ${
              downloading
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-ink text-white'
            }`}
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            PDF
          </button>
          <button onClick={onClose} aria-label="Close">
            <X size={28} />
          </button>
        </div>
      </div>
      {downloadError && (
        <div className="border border-ink/10 rounded-lg bg-red-500 text-white p-3 m-5 font-bold text-sm">
          {downloadError}
        </div>
      )}

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <Detail label="Organisation" value={lead.organisation_name} />
          <Detail label="Role" value={lead.role} />
          <Detail label="Email" value={lead.email} />
          <Detail label="Submitted" value={formatDate(lead.submitted_at)} />
          <Detail label="Overall score" value={`${lead.overall_percent}%`} />
          <Detail label="Maturity" value={lead.maturity_level} />
        </div>

        {lead.category_scores?.length > 0 && (
          <div>
            <h3 className="font-bold tracking-tight text-lg mb-3">Category scores</h3>
            <div className="space-y-2">
              {lead.category_scores.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between border border-ink/10 rounded-lg bg-white px-4 py-2 font-bold"
                >
                  <span>{c.name}</span>
                  <span>{c.percent}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {lead.ai_summary?.maturity_summary && (
          <div className="border border-ink/10 rounded-lg bg-ink text-white p-5">
            <h3 className="font-bold tracking-tight text-lg mb-2 text-lime">Adviser summary</h3>
            <p className="font-medium">{lead.ai_summary.maturity_summary}</p>
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <p className="font-bold uppercase text-xs tracking-wide text-gray-500">{label}</p>
    <p className="font-medium break-words">{value || '—'}</p>
  </div>
);

export default Leads;
